import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import Button from "../../../components/ACS-Dashboard/ACS-Blocks/Button";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import {
  addTab,
  removeTab,
  setActiveTab,
} from "../../../redux/slices/carrierSourcingSlice";
import { carrierSourcingApi } from "./api/carrierSourcingApi";
import {
  PIPELINE_STAGE_OPTIONS,
  STAGE_API,
  apiErrorMessage,
  mapPipelineRow,
  splitCityState,
} from "./mappers";
import PipelineGrid from "./PipelineGrid";
import CarrierProfile from "./CarrierProfile";
import Reporting from "./Reporting";
import AddNewCarrier, { carrierToEditForm } from "./AddNewCarrier";
import NewRateRequest from "./NewRateRequest";
import LogActivityModal from "./LogActivityModal";

const DEFAULT_SORT = "lastActivityAt";

const CarrierSourcing = () => {
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(
    selectModuleTabs("carrierSourcing")
  );

  const [isOverviewCollapsed, setIsOverviewCollapsed] = useState(true);
  const [carriers, setCarriers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [stageFilter, setStageFilter] = useState("All Stages");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [pipelineTick, setPipelineTick] = useState(0);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);
  const [ascending, setAscending] = useState(false);

  const [logModal, setLogModal] = useState(null); // { id, name }
  const [activityRefresh, setActivityRefresh] = useState({
    carrierId: null,
    tick: 0,
  });
  const [profileRefresh, setProfileRefresh] = useState({
    carrierId: null,
    tick: 0,
  });

  const loadPipeline = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const sortKey = sortBy || DEFAULT_SORT;
      const res = await carrierSourcingApi.getPipeline({
        stage: STAGE_API[stageFilter] || undefined,
        search: appliedSearch || undefined,
        page: Math.max(0, currentPage - 1),
        size: itemsPerPage,
        sort: `${sortKey},${ascending ? "asc" : "desc"}`,
      });
      const page = res.data || {};
      const content = page.content || page.data || [];
      setCarriers(content.map(mapPipelineRow));
      setTotalCount(page.totalElements ?? content.length);
      setTotalPages(Math.max(1, page.totalPages ?? 1));
    } catch (err) {
      setCarriers([]);
      setTotalCount(0);
      setTotalPages(1);
      setErrorMessage(apiErrorMessage(err, "Failed to load pipeline"));
    } finally {
      setLoading(false);
    }
  }, [
    stageFilter,
    appliedSearch,
    currentPage,
    itemsPerPage,
    sortBy,
    ascending,
    pipelineTick,
  ]);

  const handleSort = (nextSortBy) => {
    if (!nextSortBy) return;
    if (sortBy !== nextSortBy) {
      setSortBy(nextSortBy);
      setAscending(true);
    } else {
      setAscending((prev) => !prev);
    }
    setCurrentPage(1);
  };

  useEffect(() => {
    loadPipeline();
  }, [loadPipeline]);

  const openCarrierTab = (carrierId) => {
    const row = carriers.find((c) => c.id === String(carrierId));
    const officeCode =
      row?.officeCode && row.officeCode !== "—" ? row.officeCode : undefined;
    dispatch(
      addTab({
        id: `view-${carrierId}`,
        key: String(carrierId),
        name: row?.name || `Carrier ${carrierId}`,
        contentType: "carrierView",
        carrierId: String(carrierId),
        officeCode,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const openNewCarrierTab = () => {
    dispatch(
      addTab({
        id: `new-carrier-${Date.now()}`,
        name: "Add Carrier",
        contentType: "newCarrier",
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const openNewRateTab = (preset = null) => {
    const carrierId = preset?.carrierId != null ? String(preset.carrierId) : null;
    dispatch(
      addTab({
        id: carrierId ? `new-rate-${carrierId}` : `new-rate-${Date.now()}`,
        key: carrierId ? `rate-${carrierId}` : undefined,
        name: preset?.carrierName
          ? `Add Rate · ${preset.carrierName}`
          : "Add Rate",
        contentType: "newRateRequest",
        carrierId,
        carrierName: preset?.carrierName || undefined,
        officeCode: preset?.officeCode || undefined,
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const openEditCarrierTab = (carrier) => {
    if (!carrier?.rawId) return;
    dispatch(
      addTab({
        id: `edit-${carrier.rawId}`,
        key: `edit-${carrier.rawId}`,
        name: `Edit · ${carrier.name || "Carrier"}`,
        contentType: "editCarrier",
        carrierId: String(carrier.rawId),
        officeCode:
          carrier.officeCode && carrier.officeCode !== "—"
            ? carrier.officeCode
            : undefined,
        initialForm: carrierToEditForm(carrier),
        isDynamic: true,
        createdAt: Date.now(),
      })
    );
  };

  const closeActiveDynamicTab = () => {
    if (activeTab >= defaultTabsCount) {
      dispatch(removeTab({ tabIndex: activeTab }));
    } else {
      dispatch(setActiveTab({ index: 0 }));
    }
  };

  const handleAddCarrier = async (form) => {
    try {
      const equipment = (form.equipment || []).filter(Boolean);
      const coverage = form.cityState?.trim();
      const body = {
        legalName: form.legalName.trim(),
        dbaName: form.dba?.trim() || undefined,
        mcNumber: form.mc?.trim() || undefined,
        dotNumber: form.dot?.trim() || undefined,
        // Backend stores these as MySQL JSON arrays
        equipmentTypes: equipment.length ? equipment : undefined,
        fleetSize: form.fleetSize ? Number(form.fleetSize) : undefined,
        coverageRegions: coverage ? [coverage] : undefined,
        notes: form.notes?.trim() || undefined,
        contactName: form.contactName?.trim() || undefined,
        contactEmail: form.contactEmail?.trim() || undefined,
        contactPhone: form.contactPhone?.trim() || undefined,
      };
      const res = await carrierSourcingApi.createCarrier(body);
      const created = res.data;
      closeActiveDynamicTab();
      setPipelineTick((t) => t + 1);
      dispatch(setActiveTab({ index: 0 }));
      notify.success({
        title: "Carrier added",
        message: "Added to Prospecting.",
      });
      if (created?.id != null) {
        dispatch(
          addTab({
            id: `view-${created.id}`,
            key: String(created.id),
            name: created.legalName || form.legalName.trim(),
            contentType: "carrierView",
            carrierId: String(created.id),
            isDynamic: true,
            createdAt: Date.now(),
          })
        );
      }
    } catch (err) {
      notify.error({
        title: "Create failed",
        message: apiErrorMessage(err),
      });
      throw err;
    }
  };

  const handleUpdateCarrier = async (carrierId, form, officeCode) => {
    try {
      const equipment = (form.equipment || []).filter(Boolean);
      const coverage = form.cityState?.trim();
      const body = {
        legalName: form.legalName.trim(),
        dbaName: form.dba?.trim() || "",
        mcNumber: form.mc?.trim() || "",
        dotNumber: form.dot?.trim() || "",
        equipmentTypes: equipment,
        fleetSize: form.fleetSize ? Number(form.fleetSize) : null,
        coverageRegions: coverage ? [coverage] : [],
        notes: form.notes?.trim() || "",
        contactName: form.contactName?.trim() || "",
        contactEmail: form.contactEmail?.trim() || "",
        contactPhone: form.contactPhone?.trim() || "",
        contactTitle: form.contactRole?.trim() || "",
      };
      const res = await carrierSourcingApi.updateCarrier(
        Number(carrierId),
        body,
        officeCode
      );
      const updated = res.data;
      closeActiveDynamicTab();
      setPipelineTick((t) => t + 1);
      setProfileRefresh((prev) => ({
        carrierId: String(carrierId),
        tick: (prev.tick || 0) + 1,
      }));
      notify.success({
        title: "Profile updated",
        message: "Carrier details saved.",
      });
      dispatch(
        addTab({
          id: `view-${carrierId}`,
          key: String(carrierId),
          name: updated?.legalName || form.legalName.trim(),
          contentType: "carrierView",
          carrierId: String(carrierId),
          officeCode,
          isDynamic: true,
          createdAt: Date.now(),
        })
      );
    } catch (err) {
      notify.error({
        title: "Update failed",
        message: apiErrorMessage(err),
      });
      throw err;
    }
  };

  const handleRateRequest = async (payload) => {
    try {
      const origin = splitCityState(payload.origin);
      const destination = splitCityState(payload.destination);
      const equipmentList = Array.isArray(payload.equipment)
        ? payload.equipment.filter(Boolean)
        : [payload.equipment].filter(Boolean);
      const rateTypeList = Array.isArray(payload.rateTypes)
        ? payload.rateTypes.filter(Boolean)
        : [payload.laneType].filter(Boolean);
      const body = {
        originCity: origin.city,
        originState: origin.state || undefined,
        destinationCity: destination.city,
        destinationState: destination.state || undefined,
        // Backend stores a single equipmentType string; join multi-select values
        equipmentType: equipmentList.join(", ") || undefined,
        requestedRate: payload.askRate ? Number(payload.askRate) : undefined,
        notes: [
          rateTypeList.length ? `Rate type: ${rateTypeList.join(", ")}` : null,
          payload.fuelTerms,
          payload.message,
        ]
          .filter(Boolean)
          .join(" · "),
      };
      const res = await carrierSourcingApi.createRateRequest(
        Number(payload.carrierId),
        body
      );
      closeActiveDynamicTab();
      setPipelineTick((t) => t + 1);
      notify.success({
        title: "Rate request sent",
        message: "Draft rate version created.",
      });
      const id = res.data?.id || payload.carrierId;
      dispatch(
        addTab({
          id: `view-${id}`,
          key: String(id),
          name: res.data?.legalName || "Carrier",
          contentType: "carrierView",
          carrierId: String(id),
          isDynamic: true,
          createdAt: Date.now(),
        })
      );
    } catch (err) {
      notify.error({
        title: "Rate request failed",
        message: apiErrorMessage(err),
      });
      throw err;
    }
  };

  const handleLogActivity = async ({ type, summary }) => {
    if (!logModal?.id) return;
    const carrierId = Number(logModal.id);
    const entryType = String(type || "NOTE").toUpperCase();
    try {
      const threadsRes = await carrierSourcingApi.listThreads(carrierId);
      let thread =
        (threadsRes.data || []).find((t) => t.scope === "CARRIER") ||
        (threadsRes.data || [])[0];

      if (!thread) {
        const created = await carrierSourcingApi.createThread(carrierId, {
          scope: "CARRIER",
          subject: "General — Onboarding & Relationship",
          summary: "General relationship thread",
        });
        thread = created.data;
      }

      await carrierSourcingApi.addThreadEntry(carrierId, thread.id, {
        entryType: ["CALL", "EMAIL", "MEETING", "NOTE"].includes(entryType)
          ? entryType
          : "NOTE",
        summary,
        details: summary,
      });

      setLogModal(null);
      notify.success({
        title: "Activity logged",
        message: "Note added to carrier timeline.",
      });
      setPipelineTick((t) => t + 1);
      setActivityRefresh({
        carrierId: String(carrierId),
        tick: Date.now(),
      });
    } catch (err) {
      notify.error({
        title: "Log activity failed",
        message: apiErrorMessage(err),
      });
    }
  };

  const renderTabContent = (tab) => {
    if (tab.contentType === "pipeline" || tab.id === "pipeline") {
      return (
        <PipelineGrid
          carriers={carriers}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          loading={loading}
          errorMessage={errorMessage}
          onPageChange={setCurrentPage}
          onItemsPerPageChange={(n) => {
            setItemsPerPage(n);
            setCurrentPage(1);
          }}
          sortBy={sortBy}
          ascending={ascending}
          onSort={handleSort}
          onOpenCarrier={openCarrierTab}
        />
      );
    }
    if (tab.contentType === "reporting" || tab.id === "reporting") {
      return <Reporting />;
    }
    if (tab.contentType === "carrierView") {
      const activityTick =
        activityRefresh.carrierId === String(tab.carrierId)
          ? activityRefresh.tick
          : 0;
      const profileTick =
        profileRefresh.carrierId === String(tab.carrierId)
          ? profileRefresh.tick
          : 0;
      return (
        <CarrierProfile
          carrierId={tab.carrierId}
          officeCode={tab.officeCode}
          refreshKey={activityTick + profileTick}
          onLogActivity={(id, name) => setLogModal({ id, name })}
          onEditProfile={openEditCarrierTab}
          onAddRate={(carrier) =>
            openNewRateTab({
              carrierId: carrier.rawId,
              carrierName: carrier.name,
              officeCode: carrier.officeCode,
            })
          }
        />
      );
    }
    if (tab.contentType === "newCarrier") {
      return (
        <AddNewCarrier
          onCancel={closeActiveDynamicTab}
          onSave={handleAddCarrier}
        />
      );
    }
    if (tab.contentType === "editCarrier") {
      return (
        <AddNewCarrier
          key={`${tab.id}-${tab.createdAt || ""}`}
          mode="edit"
          initialValues={tab.initialForm}
          onCancel={closeActiveDynamicTab}
          onSave={(form) =>
            handleUpdateCarrier(tab.carrierId, form, tab.officeCode)
          }
        />
      );
    }
    if (tab.contentType === "newRateRequest") {
      return (
        <NewRateRequest
          initialCarrierId={tab.carrierId}
          initialCarrierName={tab.carrierName}
          onCancel={closeActiveDynamicTab}
          onSend={handleRateRequest}
        />
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-0 bg-custom-bg-purple w-full flex-1 h-full min-h-0 max-w-full">
      <div
        className="pl-fluid-2 rounded-xl w-fit flex items-center gap-1 mt-fluid-2 cursor-pointer select-none"
        onClick={() => setIsOverviewCollapsed((prev) => !prev)}
      >
        <h4 className="text-fluid-lg font-semibold text-acs-light-text dark:text-acs-dark-text transition-none">
          Carrier Sourcing Overview
        </h4>
        {isOverviewCollapsed ? (
          <ChevronRight className="w-[18px] h-[18px] text-[#223688] dark:text-[#6DE1C2] transition-none" />
        ) : (
          <ChevronDown className="w-[18px] h-[18px] text-[#223688] dark:text-[#6DE1C2] transition-none" />
        )}
      </div>

      <div className="fluid-0.5 flex flex-col flex-1 min-h-0 overflow-hidden">
        <div className="p-fluid-1 shadow-md flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden">
          {/* Toolbar on purple overview header — same as Create Shipment */}
          <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
            <div className="flex flex-wrap items-center gap-2">
              <FilterButton
                text={stageFilter}
                options={PIPELINE_STAGE_OPTIONS}
                selectedValue={stageFilter}
                onSelect={(value) => {
                  setStageFilter(value);
                  setCurrentPage(1);
                }}
                backgroundColor="white"
                textColor="black"
              />
              <SearchBox
                value={searchInput}
                onChange={setSearchInput}
                onSearch={() => {
                  setAppliedSearch(searchInput);
                  setCurrentPage(1);
                }}
                placeholder="Search Carriers"
              />
              <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
                {totalCount} Carriers
              </span>
              <button
                type="button"
                onClick={() => {
                  setSearchInput("");
                  setAppliedSearch("");
                  setStageFilter("All Stages");
                  setSortBy(DEFAULT_SORT);
                  setAscending(false);
                  setCurrentPage(1);
                  setPipelineTick((t) => t + 1);
                }}
                className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
                title="Reload page"
                aria-label="Reload page"
              >
                <RefreshCw
                  size={16}
                  className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${
                    loading ? "animate-spin" : "hover:text-gray-700"
                  }`}
                />
              </button>
            </div>

            <div className="flex items-center gap-2">
              <Button
                text="Add Carrier"
                onClick={openNewCarrierTab}
                backgroundColor="#223688"
                textColor="white"
                fontSize="14px"
                padding="10px 18px"
                borderRadius="24px"
              />
            </div>
          </div>

          <div className="flex-1 mt-fluid-2 min-h-0 flex flex-col scrollbar-hide">
            <Tabs
              tabs={tabs.map((t) => t.name)}
              tabContents={tabs.map((tab) => (
                <div key={tab.id} className="h-full min-h-0 flex flex-col">
                  {renderTabContent(tab)}
                </div>
              ))}
              activeTab={activeTab}
              onTabChange={(index) => dispatch(setActiveTab({ index }))}
              isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
              onRemoveTab={(index) => dispatch(removeTab({ tabIndex: index }))}
              keepAlive={tabs.map(
                (tab) =>
                  tab.contentType === "pipeline" ||
                  tab.contentType === "reporting" ||
                  tab.id === "pipeline" ||
                  tab.id === "reporting"
              )}
            />
          </div>
        </div>
      </div>

      {logModal ? (
        <LogActivityModal
          carrierName={logModal.name}
          onClose={() => setLogModal(null)}
          onSave={handleLogActivity}
        />
      ) : null}
    </div>
  );
};

export default CarrierSourcing;
